var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useState } from 'react';
import clsx from 'clsx';
import { KeyAttributeValue, useClasses, useControlledStateWarning, useLayoutProps, useProps, } from '@hawkins/helpers';
import { IconTime } from '@hawkins/icons/lib/iconTime';
import { DropdownTrigger } from '../_private/dropdownTrigger';
import { Popover } from '../popover';
import { TimePicker } from './timePicker';
import { defaultFormatTime } from './timePicker.utils';
import { useTimePickerDropdownStyles } from './timePickerDropdown.styles';
import { TimePickerDropdownTestIds } from './timePickerDropdown.testids';
var displayName = 'TimePickerDropdown';
/**
 * `<TimePickerDropdown>` renders a `<TimePicker>` in the context of a dropdown toggle.
 * Unlike `<TimePickerInput>`, this component does not allow the user to type in a value.
 */
export var TimePickerDropdown = forwardRef(function (props, ref) {
    var _a;
    // Props
    var _b = useProps(displayName, props), classesProp = _b.classes, className = _b.className, _c = _b.clearable, clearable = _c === void 0 ? false : _c, _d = _b.component, Component = _d === void 0 ? 'div' : _d, dateProp = _b.date, _e = _b.defaultDate, defaultDate = _e === void 0 ? null : _e, _f = _b.defaultOpen, defaultOpen = _f === void 0 ? false : _f, _g = _b.disabled, disabled = _g === void 0 ? false : _g, formatTime = _b.formatTime, _h = _b.fullWidth, fullWidth = _h === void 0 ? false : _h, layout = _b.layout, onChange = _b.onChange, onToggle = _b.onToggle, openProp = _b.open, popoverProps = _b.popoverProps, styleProp = _b.style, _j = _b.timePickerProps, _k = _j === void 0 ? {} : _j, _l = _k.onKeyDown, onTimePickerKeyDownProp = _l === void 0 ? undefined : _l, timePickerProps = __rest(_k, ["onKeyDown"]), restProps = __rest(_b, ["classes", "className", "clearable", "component", "date", "defaultDate", "defaultOpen", "disabled", "formatTime", "fullWidth", "layout", "onChange", "onToggle", "open", "popoverProps", "style", "timePickerProps"]);
    useControlledStateWarning(dateProp, { displayName: displayName });
    var _m = timePickerProps.isUTC, isUTC = _m === void 0 ? false : _m, _o = timePickerProps.use12HourClock, use12HourClock = _o === void 0 ? false : _o, _p = timePickerProps.withSeconds, withSeconds = _p === void 0 ? false : _p;
    var isDateControlled = dateProp !== undefined;
    var isOpenControlled = openProp !== undefined;
    // State
    var _q = __read(useState(defaultDate), 2), internalDate = _q[0], setInternalDate = _q[1];
    var _r = __read(useState(defaultOpen), 2), internalOpen = _r[0], setInternalOpen = _r[1];
    var date = isDateControlled ? dateProp : internalDate;
    var open = isOpenControlled ? openProp : internalOpen;
    var toggleDropdown = useCallback(function (isOpen) {
        if (!isOpenControlled)
            setInternalOpen(isOpen);
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(isOpen);
    }, [isOpenControlled, onToggle]);
    var onDropdownTriggerToggle = useCallback(function () {
        toggleDropdown(!open);
    }, [toggleDropdown, open]);
    var onTimePickerChange = useCallback(function (selectedTime) {
        if (!isDateControlled)
            setInternalDate(selectedTime);
        onChange === null || onChange === void 0 ? void 0 : onChange(selectedTime);
    }, [isDateControlled, onChange]);
    var clearValue = useCallback(function () {
        onTimePickerChange(null);
    }, [onTimePickerChange]);
    var onTimePickerKeyDown = useCallback(function (ev) {
        if (ev.key === KeyAttributeValue.ENTER)
            toggleDropdown(false);
        onTimePickerKeyDownProp === null || onTimePickerKeyDownProp === void 0 ? void 0 : onTimePickerKeyDownProp(ev);
    }, [onTimePickerKeyDownProp, toggleDropdown]);
    // Styles
    var baseClasses = useTimePickerDropdownStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_a = {},
        _a[classes.rootFullWidth] = fullWidth,
        _a));
    return (_jsx(Popover, __assign({ disableActiveStyling: true, disabled: disabled, onToggle: toggleDropdown, open: open, trigger: _jsx(DropdownTrigger, __assign({ className: rootClassName, clearable: clearable, component: Component, "data-testid": TimePickerDropdownTestIds.ROOT, disabled: disabled, hasValue: !!date, onClear: clearValue, onToggle: onDropdownTriggerToggle, open: open, ref: ref, startAdornment: _jsx(IconTime, { className: classes.startAdornmentIcon, size: "small" }), style: style }, restProps, { children: (formatTime || defaultFormatTime)(date, { isUTC: isUTC, use12HourClock: use12HourClock, withSeconds: withSeconds }) })) }, popoverProps, { children: _jsx(TimePicker, __assign({ onKeyDown: onTimePickerKeyDown }, timePickerProps, { date: date, disabled: disabled, onChange: onTimePickerChange })) })));
});
TimePickerDropdown.displayName = displayName;
